import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown, LogOut, User as UserIcon, Shield } from "lucide-react";
import { Logo } from "./Logo";
import LanguageToggle from "./LanguageToggle";
import NotificationBell from "./NotificationBell";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "./ui/dropdown-menu";
import { useAuth } from "../contexts/AuthContext";

export default function Header() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Organised by what people come to do, not by content type. Funding and Tools
  // are top-level (they were buried under "Resources"). Home lives on the logo;
  // About and the persona pages (founders/investors/SME) live in the footer, so
  // nothing here is unreachable. No URL changed.
  const NAV = [
    { to: "/startups", label: t("nav.startups") },
    { to: "/opportunites", label: t("nav.opportunities") },
    { to: "/outils", label: t("nav.tools") },
    { label: t("nav.resources"), items: [
      { to: "/actualites", label: t("nav.news") },
      { to: "/knowledge", label: t("nav.knowledge") },
      { to: "/ecosystem", label: t("nav.ecosystem") },
      { to: "/mentors", label: t("nav.mentors") },
      { to: "/services", label: t("nav.services") },
      { to: "/jobs", label: t("nav.jobs") },
      { to: "/events", label: t("nav.events") },
    ] },
    { to: "/community", label: t("nav.community") },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const initials = (user?.name || user?.email || "U").slice(0, 1).toUpperCase();

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/70 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <Logo />
        </div>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((entry) => entry.items ? (
            <DropdownMenu key={entry.label}>
              <DropdownMenuTrigger asChild>
                <button className="navlink inline-flex items-center gap-1 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors outline-none" data-testid={`nav-group-${entry.label}`}>
                  {entry.label} <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {entry.items.map((it) => (
                  <DropdownMenuItem key={it.to} onClick={() => navigate(it.to)} data-testid={`nav-${it.to.slice(1)}`}>{it.label}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <NavLink
              key={entry.to}
              to={entry.to}
              data-testid={`nav-${entry.to.slice(1)}`}
              className={({ isActive }) => `navlink text-sm font-medium hover:text-foreground transition-colors ${isActive ? "text-foreground" : "text-foreground/80"}`}
            >
              {entry.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageToggle />
          {user && <NotificationBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-2 py-1 text-sm hover:border-primary/40 transition-colors btn-lift">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--primary))] text-white text-xs font-semibold">{initials}</span>
                  <span className="max-w-[120px] truncate">{user.name || user.email}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{user.name || user.email}</span>
                    <span className="text-xs text-muted-foreground capitalize">{user.role}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <UserIcon className="mr-2 h-4 w-4" /> {t("nav.dashboard")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <UserIcon className="mr-2 h-4 w-4" /> {t("nav.profile")}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <Shield className="mr-2 h-4 w-4" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" data-testid="header-login">
                <Link to="/login">{t("nav.login")}</Link>
              </Button>
              <Button asChild size="sm" data-testid="header-signup" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white btn-lift">
                <Link to="/signup">{t("nav.signup")}</Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="flex items-center gap-2 lg:hidden">
          {user && <NotificationBell />}
          <LanguageToggle />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-nav-toggle">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] sm:w-96 overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <Logo />
                <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <nav className="flex flex-col gap-1">
                {NAV.map((entry) => entry.items ? (
                  <div key={entry.label} className="py-1">
                    <p className="px-3 pt-2 pb-1 text-xs uppercase tracking-wide text-muted-foreground">{entry.label}</p>
                    {entry.items.map((it) => (
                      <Link key={it.to} to={it.to} onClick={() => setOpen(false)} className="block px-3 py-2 rounded-md hover:bg-secondary text-sm font-medium" data-testid={`mobile-nav-${it.to.slice(1)}`}>{it.label}</Link>
                    ))}
                  </div>
                ) : (
                  <Link key={entry.to} to={entry.to} onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-md hover:bg-secondary text-sm font-medium" data-testid={`mobile-nav-${entry.to.slice(1)}`}>{entry.label}</Link>
                ))}
              </nav>
              <div className="mt-6 border-t border-border pt-6 flex flex-col gap-2">
                {user ? (
                  <>
                    <Button onClick={() => { setOpen(false); navigate("/dashboard"); }} variant="outline">{t("nav.dashboard")}</Button>
                    <Button onClick={() => { setOpen(false); navigate("/profile"); }} variant="outline">{t("nav.profile")}</Button>
                    {user.role === "admin" && (
                      <Button onClick={() => { setOpen(false); navigate("/admin"); }} variant="outline">Admin</Button>
                    )}
                    <Button onClick={() => { setOpen(false); handleLogout(); }} variant="ghost">
                      <LogOut className="h-4 w-4 mr-2" /> {t("nav.logout")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button asChild variant="outline" onClick={() => setOpen(false)}>
                      <Link to="/login">{t("nav.login")}</Link>
                    </Button>
                    <Button asChild className="bg-[hsl(var(--accent))] text-white" onClick={() => setOpen(false)}>
                      <Link to="/signup">{t("nav.signup")}</Link>
                    </Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
